import { useState, useEffect, useRef } from 'react';
import './App.css';
import axios from 'axios';
import { Copy, Share2, Sparkles, Shuffle, X, ChevronDown, HelpCircle, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import VibeStoryTour from './VibeStoryTour';

const LOCAL_API_ORIGIN = ['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'https://vibes.terencela.com' : '';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || LOCAL_API_ORIGIN;
const API = `${BACKEND_URL}/api`;

const EMAIL_GATE_STORAGE_KEY = 'vibeEmailGatewayV3';
const EMAIL_STORAGE_KEY = 'vibeEmailV1';
const MAX_EMAIL_LENGTH = 254;

function normalizeEmail(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim()
    .toLowerCase();
}

function isPlausibleEmail(raw) {
  const email = normalizeEmail(raw);
  if (!email || email.length > MAX_EMAIL_LENGTH || /[\s\x00-\x1F\x7F]/.test(email)) return false;

  const at = email.indexOf('@');
  if (at < 1 || at !== email.lastIndexOf('@')) return false;

  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const domainLabels = domain.split('.');
  return (
    local.length <= 64 &&
    /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+$/i.test(local) &&
    !local.startsWith('.') &&
    !local.endsWith('.') &&
    !local.includes('..') &&
    domain.length >= 2 &&
    domainLabels.every(label => (
      label.length >= 1 &&
      label.length <= 63 &&
      /^[a-z0-9-]+$/i.test(label) &&
      !label.startsWith('-') &&
      !label.endsWith('-')
    ))
  );
}

const TRANSLATION_MODES = [
  // 💼 Corporate Cringe — highest viral probability
  { id: 'linkedin_cringe', name: 'LinkedIn Cringe', category: '💼 Corporate Cringe' },
  { id: 'finance_bro', name: 'Finance Bro', category: '💼 Corporate Cringe' },
  { id: 'tech_bro', name: 'Tech Bro', category: '💼 Corporate Cringe' },
  { id: 'elon_musk', name: 'Elon Musk Mode', category: '🎤 Celebrity Vibes' },
  { id: 'david_attenborough', name: 'David Attenborough', category: '🎤 Celebrity Vibes' },
  { id: 'michael_scott', name: 'Michael Scott', category: '🎤 Celebrity Vibes' },
  { id: 'patrick_bateman', name: 'Patrick Bateman', category: '🎤 Celebrity Vibes' },
  { id: 'walter_white', name: 'Walter White', category: '🎤 Celebrity Vibes' },
  { id: 'saul_goodman', name: 'Saul Goodman', category: '🎤 Celebrity Vibes' },
  { id: 'homelander', name: 'Homelander', category: '🎤 Celebrity Vibes' },
  { id: 'shrek', name: 'Shrek', category: '🎤 Celebrity Vibes' },
  { id: 'spongebob', name: 'SpongeBob', category: '🎤 Celebrity Vibes' },
  { id: 'gollum', name: 'Gollum', category: '🎤 Celebrity Vibes' },
  { id: 'darth_vader', name: 'Darth Vader', category: '🎤 Celebrity Vibes' },
  { id: 'plain_to_corporate', name: 'Plain → Corporate Speak', category: '💼 Corporate Cringe' },
  { id: 'founder_mode', name: 'Founder Mode', category: '💼 Corporate Cringe' },
  { id: 'startup_decoder', name: 'Startup → Reality', category: '💼 Corporate Cringe', kind: 'decode' },
  { id: 'corporate_decoder', name: 'Corporate → Reality', category: '💼 Corporate Cringe', kind: 'decode' },
  { id: 'marketing_decoder', name: 'Marketing → Reality', category: '💼 Corporate Cringe', kind: 'decode' },
  { id: 'investor_decoder', name: 'VC → Reality', category: '💼 Corporate Cringe', kind: 'decode' },
  { id: 'investor_speak', name: 'VC Speak', category: '💼 Corporate Cringe' },
  { id: 'mbb_consultant', name: 'MBB Consultant', category: '💼 Corporate Cringe' },
  { id: 'plain_to_marketing', name: 'Plain → Marketing Fluff', category: '💼 Corporate Cringe' },
  { id: 'casual_to_formal', name: 'Casual → Formal', category: '💼 Corporate Cringe' },
  { id: 'ai_to_vc', name: 'Idea → Investor Pitch', category: '💼 Corporate Cringe' },
  // 🔥 Gen Z & Internet
  { id: 'wall_street_bets', name: 'Wall Street Bets', category: '🔥 Gen Z & Internet' },
  { id: 'plain_to_therapy', name: 'Plain → Therapy Speak', category: '🔥 Gen Z & Internet' },
  { id: 'normal_to_rizz', name: 'Add Rizz', category: '🔥 Gen Z & Internet' },
  { id: 'professional_to_meme', name: 'Meme Speak', category: '🔥 Gen Z & Internet' },
  { id: 'human_to_alpha', name: 'Alpha Male', category: '🔥 Gen Z & Internet' },
  { id: 'sober_to_drunk', name: 'Drunk Text', category: '🔥 Gen Z & Internet' },
  { id: 'male_to_female', name: 'Direct → Contextual', category: '🔥 Gen Z & Internet' },
  { id: 'male_to_meaning', name: 'They Said → Likely Meant', category: '🔥 Gen Z & Internet', kind: 'decode' },
  { id: 'female_to_male', name: 'Context → Likely Meaning', category: '🔥 Gen Z & Internet', kind: 'decode' },
  { id: 'therapy_decoder', name: 'Therapy Speak → Plain', category: '🔥 Gen Z & Internet', kind: 'decode' },
  { id: 'msn_chat', name: '90s MSN Chat', category: '🔥 Gen Z & Internet' },
  // 🎤 Celebrity Vibes
  { id: 'trump_decoder', name: 'Trump → Reality', category: '🎤 Celebrity Vibes', kind: 'decode' },
  { id: 'trump_speak', name: 'Trump Mode', category: '🎤 Celebrity Vibes' },
  { id: 'ai_doomer', name: 'AI Doomer (reply)', category: '🎤 Celebrity Vibes', kind: 'reply' },
  { id: 'techno_optimist', name: 'Techno-Optimist (reply)', category: '🎤 Celebrity Vibes', kind: 'reply' },
  { id: 'hormozi_mode', name: 'Hormozi (reply)', category: '🎤 Celebrity Vibes', kind: 'reply' },
  { id: 'gordon_ramsay', name: 'Gordon Ramsay (reply)', category: '🎤 Celebrity Vibes', kind: 'reply' },
  { id: 'joe_rogan', name: 'Joe Rogan (reply)', category: '🎤 Celebrity Vibes', kind: 'reply' },
  { id: 'valley_girl', name: 'Valley Girl', category: '🎤 Celebrity Vibes' },
  { id: 'reddit_aita', name: 'Reddit AITA', category: '🎤 Celebrity Vibes' },
  { id: 'asian_parent', name: 'Asian Parent (reply)', category: '🎤 Celebrity Vibes', kind: 'reply' },
  // 💬 Tone & Mood
  { id: 'anxious_to_avoidant', name: 'Anxious → Avoidant', category: '💬 Tone & Mood' },
  { id: 'avoidant_to_anxious', name: 'Avoidant → Anxious', category: '💬 Tone & Mood' },
  { id: 'angry_to_calm', name: 'Angry → Calm', category: '💬 Tone & Mood' },
  { id: 'mean_to_nice', name: 'Mean → Nice', category: '💬 Tone & Mood' },
  { id: 'serious_to_sarcastic', name: 'Serious → Sarcastic', category: '💬 Tone & Mood' },
  { id: 'passive_to_direct', name: 'Passive → Direct', category: '💬 Tone & Mood' },
  { id: 'rough_to_polite', name: 'Rough → Polite', category: '💬 Tone & Mood' },
  // 👴 Generational & Historical
  { id: 'genz_to_boomer', name: 'Gen Z → Boomer', category: '👴 Generational' },
  { id: 'boomer_to_genz', name: 'Boomer → Gen Z', category: '👴 Generational' },
  { id: 'medieval_royal', name: 'Medieval Royal', category: '👴 Generational' },
  // 🌍 Cultural Vibes
  { id: 'bunzli', name: 'Swiss Bünzli', category: '🌍 Cultural Vibes' },
  { id: 'german_spiesser', name: 'German Spießer', category: '🌍 Cultural Vibes' },
];

// Dynamic placeholder: tells user what to type for each mode
const MODE_HINTS = {
  linkedin_cringe:    "Type anything — we'll LinkedIn-cringe it...",
  finance_bro:        "Type anything — we'll make it Wall Street elite...",
  wall_street_bets:   "Type anything — we'll YOLO it into WSB chaos...",
  tech_bro:           "Type anything — we'll Silicon Valley-ify it...",
  elon_musk:          "Type anything — we'll make it a civilization-scale mission...",
  david_attenborough: "Type anything — we'll narrate it like rare wildlife...",
  michael_scott:      "Type anything — we'll turn it into an awkward office announcement...",
  patrick_bateman:    "Type anything — we'll turn one detail into a status emergency...",
  walter_white:       "Type anything — we'll make it a controlled escalation...",
  saul_goodman:       "Type anything — we'll find the loophole and sell it back to you...",
  homelander:         "Type anything — we'll make a tiny win sound terrifyingly presidential...",
  shrek:              "Type anything — we'll run it through uncompromising swamp logic...",
  spongebob:          "Type anything — we'll celebrate it like the best day ever...",
  gollum:             "Type anything — we'll make the real problem precious and suspicious...",
  darth_vader:        "Type anything — we'll treat it as an imperial operational failure...",
  plain_to_corporate: "Type anything — we'll turn it into corporate speak...",
  founder_mode:       "Type anything — we'll make it founder delusional...",
  startup_decoder:    "Type startup jargon — we'll say what it really means...",
  corporate_decoder:  "Paste corporate speak — we'll say what it really means...",
  marketing_decoder:  "Paste marketing copy — we'll remove the hype...",
  investor_decoder:   "Paste the VC message — we'll decode the hedge...",
  investor_speak:     "Type anything — we'll VC-ify it...",
  mbb_consultant:     "Type anything — we'll MECE and synthesize it...",
  plain_to_marketing: "Type anything — we'll turn it into brand marketing copy...",
  casual_to_formal:   "Type anything casual — we'll make it professional...",
  ai_to_vc:           "Type your idea — we'll turn it into a VC pitch...",
  normal_to_rizz:     "Type anything — we'll add the rizz...",
  professional_to_meme:"Type anything — we'll meme-ify it...",
  human_to_alpha:     "Type anything — we'll alpha-male it...",
  sober_to_drunk:     "Type anything — we'll drunk-text it...",
  male_to_female:     "Type something direct — we'll make it warmer and clearer...",
  male_to_meaning:    "Type what they said — we'll give a likely reading...",
  female_to_male:     "Paste the message — we'll give a context-aware likely reading...",
  therapy_decoder:    "Paste the therapy speak — we'll make it plain...",
  plain_to_therapy:   "Type anything — we'll therapy-speak it...",
  msn_chat:           "Type anything — we'll turn it into 90s MSN lingo...",
  anxious_to_avoidant:"Type something anxious or clingy...",
  avoidant_to_anxious:"Type something casual or distant...",
  angry_to_calm:      "Type something angry or frustrated...",
  mean_to_nice:       "Type something harsh — we'll soften it...",
  serious_to_sarcastic:"Type something sincere or serious...",
  passive_to_direct:  "Type something vague or indirect...",
  rough_to_polite:    "Type something blunt or rude...",
  genz_to_boomer:     "Type Gen Z slang — we'll explain it to boomers...",
  boomer_to_genz:     "Type something boomer — we'll Gen Z it...",
  medieval_royal:     "Type anything — we'll make it a royal decree...",
  trump_decoder:      "Paste a Trump quote — we'll say what he actually means...",
  trump_speak:        "Type anything — Trump will make it tremendous...",
  ai_doomer:          "Say something — get a doomer reply...",
  techno_optimist:    "Say something — get an e/acc reply...",
  hormozi_mode:       "Say a complaint or goal — get Hormozi's reply...",
  gordon_ramsay:      "Say what you did — get Ramsay's reply...",
  joe_rogan:          "Say something — get Rogan's reply...",
  valley_girl:        "Type anything — we'll Valley Girl it, like...",
  reddit_aita:        "Type a situation — we'll AITA-post it...",
  asian_parent:       "Tell them something — get a parent reply...",
  bunzli:             "Type anything — the Bünzli will correct it properly...",
  german_spiesser:    "Type anything — the German Spießer will formally object...",
};

const SUGGESTIONS = [
  { text: "We fired half the team", mode: 'plain_to_corporate', label: 'Corporate speak', color: '#FFE000' },
  { text: 'Just got promoted', mode: 'tech_bro', label: 'Tech Bro It', color: '#00FF88' },
  { text: 'Send me the report now', mode: 'rough_to_polite', label: 'Make It Polite', color: '#FF0066', textColor: '#fff' },
  { text: "I'm going to the gym", mode: 'human_to_alpha', label: 'Alpha Mode', color: '#0033FF', textColor: '#fff' },
  { text: 'This meeting is pointless', mode: 'boomer_to_genz', label: 'Gen Z It', color: '#FFE000' },
  { text: 'I miss you so much', mode: 'anxious_to_avoidant', label: 'Play It Cool', color: '#00FF88' },
  { text: 'Your work was absolutely terrible', mode: 'mean_to_nice', label: 'Soften It', color: '#FF0066', textColor: '#fff' },
  { text: 'I need to process this', mode: 'plain_to_therapy', label: 'Therapy Speak', color: '#0033FF', textColor: '#fff' },
  { text: 'Want to hang out sometime?', mode: 'normal_to_rizz', label: 'Add Rizz', color: '#FFE000' },
  { text: 'I totally disagree', mode: 'serious_to_sarcastic', label: 'Sarcasm Mode', color: '#00FF88' },
  { text: 'An app that helps people find parking', mode: 'founder_mode', label: 'Founder Mode', color: '#FF0066', textColor: '#fff' },
  { text: "We're focusing on sustainable growth", mode: 'startup_decoder', label: 'Startup → Reality', color: '#00FF88' },
  { text: "Let's circle back on this", mode: 'plain_to_corporate', label: 'Corporate speak', color: '#0033FF', textColor: '#fff' },
  { text: 'I had a great lunch today', mode: 'finance_bro', label: 'Finance Bro It', color: '#00FF88' },
  { text: 'I got fired today', mode: 'linkedin_cringe', label: 'LinkedIn It', color: '#FFE000' },
  { text: 'We made a new product', mode: 'plain_to_marketing', label: 'Marketing copy', color: '#FF0066', textColor: '#fff' },
  { text: 'I want to invest in your company', mode: 'investor_speak', label: 'VC Speak', color: '#0033FF', textColor: '#fff' },
  { text: 'hey can u send me that thing', mode: 'casual_to_formal', label: 'Make It Formal', color: '#00FF88' },
  { text: 'THIS IS SO UNFAIR!!!', mode: 'angry_to_calm', label: 'Calm It Down', color: '#FFE000' },
  { text: 'I guess that could maybe work', mode: 'passive_to_direct', label: 'Just Say It', color: '#FF0066', textColor: '#fff' },
  { text: 'I am very tired today', mode: 'sober_to_drunk', label: 'Drunk Text It', color: '#0033FF', textColor: '#fff' },
  { text: 'Talk later', mode: 'avoidant_to_anxious', label: 'Make It Needy', color: '#00FF88' },
  { text: 'She absolutely killed that presentation', mode: 'genz_to_boomer', label: 'Boomer Mode', color: '#FFE000' },
  { text: 'Building an AI startup', mode: 'tech_bro', label: 'Tech Bro It', color: '#FF0066', textColor: '#fff' },
  { text: 'I just woke up', mode: 'human_to_alpha', label: 'Alpha Mode', color: '#0033FF', textColor: '#fff' },
  { text: 'We are disrupting the market', mode: 'plain_to_marketing', label: 'Marketing copy', color: '#00FF88' },
  { text: 'I am so stressed', mode: 'professional_to_meme', label: 'Meme It', color: '#FFE000' },
  { text: 'I love you', mode: 'male_to_female', label: 'Contextual Version', color: '#FF0066', textColor: '#fff' },
  { text: 'no worries bestie slay', mode: 'genz_to_boomer', label: 'Boomer Mode', color: '#0033FF', textColor: '#fff' },
  { text: 'I launched a new product', mode: 'hormozi_mode', label: 'Hormozi Mode', color: '#FFE000' },
  { text: 'My code has a bug', mode: 'gordon_ramsay', label: 'Gordon Ramsay', color: '#FF0066', textColor: '#fff' },
  { text: "It's fine, do whatever you want", mode: 'female_to_male', label: 'Likely Meaning', color: '#FF0066', textColor: '#fff' },
  { text: 'I just had coffee', mode: 'trump_speak', label: 'Trump Mode', color: '#FF0066', textColor: '#fff' },
  { text: 'I used ChatGPT today', mode: 'ai_doomer', label: 'AI Doomer', color: '#0033FF', textColor: '#fff' },
  { text: 'I used ChatGPT today', mode: 'techno_optimist', label: 'Techno-Optimist', color: '#00FF88' },
  { text: 'I just had coffee', mode: 'joe_rogan', label: 'Joe Rogan', color: '#0033FF', textColor: '#fff' },
  { text: 'I skipped the meeting', mode: 'reddit_aita', label: 'Reddit AITA', color: '#FFE000' },
  { text: 'I am going shopping', mode: 'valley_girl', label: 'Valley Girl', color: '#00FF88' },
  { text: 'I just made $10k trading', mode: 'wall_street_bets', label: 'WSB It', color: '#FF0066', textColor: '#fff' },
  { text: "I'm going to McDonald's", mode: 'medieval_royal', label: 'Medieval Royal', color: '#0033FF', textColor: '#fff' },
  { text: 'meet me after school ok', mode: 'msn_chat', label: '90s MSN Chat', color: '#00FF88' },
  { text: 'I am going to lunch', mode: 'mbb_consultant', label: 'MBB Consultant', color: '#0033FF', textColor: '#fff' },
  { text: 'I got a B on my exam', mode: 'asian_parent', label: 'Asian Parent', color: '#FFE000' },
  { text: 'I am throwing a party Saturday night', mode: 'bunzli', label: 'Swiss Bünzli', color: '#0033FF', textColor: '#fff' },
  { text: 'My neighbor is being too loud', mode: 'german_spiesser', label: 'German Spießer', color: '#00FF88' },
];

const groupedModes = TRANSLATION_MODES.reduce((acc, mode) => {
  if (!acc[mode.category]) acc[mode.category] = [];
  acc[mode.category].push(mode);
  return acc;
}, {});

const MODE_EXAMPLES = {
  female_to_male: [
    "It's fine, do whatever you want",
    "I'm not mad",
    "You don't have to",
  ],
  male_to_meaning: [
    "I'm just tired",
    "We can talk about it later",
    "I don't care where we eat",
  ],
  anxious_to_avoidant: [
    "Are we okay? You haven't replied",
    "I miss you so much",
    "Please tell me what you're thinking",
  ],
  avoidant_to_anxious: [
    "Talk later",
    "I need some space",
    "I'm busy this week",
  ],
  therapy_decoder: [
    "That doesn't serve me anymore",
    "I need to protect my energy",
    "I'm setting a boundary",
  ],
  passive_to_direct: [
    "Whatever you think is best",
    "It would have been nice to know",
    "No worries, I'll do it myself",
  ],
  angry_to_calm: [
    "YOU NEVER LISTEN TO ME",
    "This is completely ridiculous!",
    "I'm sick of fixing your mistakes",
  ],
  rough_to_polite: [
    "Send me the report now",
    "This makes no sense",
    "Stop wasting my time",
  ],
  startup_decoder: [
    "We are being thoughtful about hiring",
    "We're exploring strategic alternatives",
    "We are doubling down on our core",
  ],
  corporate_decoder: [
    "Let's circle back after we align internally",
    "We'll take this offline",
    "There are no immediate next steps",
  ],
  marketing_decoder: [
    "A revolutionary platform that transforms work",
    "Built to unlock your full potential",
    "A seamless, category-defining experience",
  ],
  investor_decoder: [
    "We'd love to stay close",
    "Come back when you have more traction",
    "The team is strong, but the timing feels early",
  ],
};

// Decode surface — focused value lanes built on top of the existing modes.
// Each lane targets one repeat-use job. `primaryMode` is the default mode pre-selected
// when the lane is opened; `submodes` let users refine without leaving the lane.
// `routeTo: 'play'` jumps to the original viral surface and full mode sheet.
const VALUE_LANES = [
  {
    id: 'decode_them',
    title: 'What did they mean?',
    sub: 'Decode the message before you reply',
    accent: '#FF0066',
    textOnAccent: '#FFFFFF',
    primaryMode: 'female_to_male',
    submodes: [
      { id: 'female_to_male', label: 'Context → likely meaning' },
      { id: 'male_to_meaning', label: 'They said → likely meant' },
      { id: 'anxious_to_avoidant', label: 'Needy → distant' },
      { id: 'avoidant_to_anxious', label: 'Distant → needy' },
    ],
    placeholder: "Paste the message — we'll tell you what it really means...",
  },
  {
    id: 'less_insane',
    title: 'Make this less insane',
    sub: 'Strip the therapy speak. Soften the rage.',
    accent: '#00FF88',
    textOnAccent: '#0A0A0A',
    primaryMode: 'therapy_decoder',
    submodes: [
      { id: 'therapy_decoder', label: 'Therapy → plain' },
      { id: 'passive_to_direct', label: 'Passive → direct' },
      { id: 'angry_to_calm', label: 'Angry → calm' },
      { id: 'rough_to_polite', label: 'Rough → polite' },
    ],
    placeholder: "Paste the message — we'll make it readable...",
  },
  {
    id: 'decode_bs',
    title: 'Decode the BS',
    sub: 'Founder, VC, marketing -- translated to reality',
    accent: '#FFE000',
    textOnAccent: '#0A0A0A',
    primaryMode: 'startup_decoder',
    submodes: [
      { id: 'startup_decoder', label: 'Startup → reality' },
      { id: 'corporate_decoder', label: 'Corporate → reality' },
      { id: 'marketing_decoder', label: 'Marketing → reality' },
      { id: 'investor_decoder', label: 'VC → reality' },
    ],
    placeholder: "Paste the corporate / startup line — we'll decode it...",
  },
  {
    id: 'make_funny',
    title: 'Make it funny',
    sub: '40+ chaos modes -- Trump, Hormozi, Valley Girl, more',
    accent: '#0033FF',
    textOnAccent: '#FFFFFF',
    routeTo: 'play',
  },
];

// --- Carousel ---
function Carousel({ onSelect }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const pendingIndex = useRef(0);

  const transitionTo = (nextIndex) => {
    const ni = (nextIndex + SUGGESTIONS.length) % SUGGESTIONS.length;
    pendingIndex.current = ni;
    setVisible(false);
    setTimeout(() => {
      setIndex(ni);
      setVisible(true);
    }, 160);
  };

  useEffect(() => {
    const id = setInterval(() => {
      transitionTo(pendingIndex.current + 1);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const card = SUGGESTIONS[index];

  return (
    <div className="relative flex items-center h-[72px] shrink-0 border-b-4 border-[#0A0A0A] overflow-hidden bg-[#F4F4F0]">
      <div className="flex-1 px-3 h-full flex items-center">
        <button
          data-testid="carousel-suggestion-card"
          onClick={() => onSelect(card)}
          style={{
            backgroundColor: card.color,
            color: card.textColor || '#0A0A0A',
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.15s ease-in-out',
          }}
          className="w-full border-4 border-[#0A0A0A] shadow-brutal-medium px-4 py-2 text-left flex items-center justify-between gap-2 relative overflow-hidden"
        >
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-display text-xs uppercase tracking-widest opacity-70 leading-none">{card.label}</span>
            <span className="font-mono text-sm leading-snug truncate">"{card.text}"</span>
          </div>
          <span className="shrink-0 font-display text-xs uppercase tracking-wider border-2 border-current px-2 py-0.5 opacity-70 animate-pulse whitespace-nowrap">try it →</span>
        </button>
      </div>

      <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1">
        {SUGGESTIONS.map((_, i) => (
          <span key={i} className={`block h-1 border border-[#0A0A0A] transition-all duration-300 ${i === index ? 'w-4 bg-[#0A0A0A]' : 'w-1.5 bg-transparent'}`} />
        ))}
      </div>
    </div>
  );
}

function SurfaceTabs({ active, onChange }) {
  const tabs = [
    { id: 'decode', label: 'TRANSLATE' },
    { id: 'play', label: 'PLAY' },
  ];
  return (
    <div className="shrink-0 flex border-b-4 border-[#0A0A0A]">
      {tabs.map(t => {
        const isActive = active === t.id;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.id)}
            className={`flex-1 py-1.5 sm:py-2 font-display text-sm uppercase tracking-wider transition-colors ${
              isActive
                ? 'bg-[#0A0A0A] text-[#FFE000]'
                : 'bg-[#F4F4F0] text-[#0A0A0A]/50 hover:bg-[#0A0A0A]/5'
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function DecodeLanes({ activeLaneId, onLaneSelect, onSubmodeSelect, selectedMode, onExampleTap, hasInput }) {
  const activeLane = VALUE_LANES.find(l => l.id === activeLaneId) || null;
  const visibleExamples = MODE_EXAMPLES[selectedMode] || [];

  return (
    <div className="shrink-0 border-b-4 border-[#0A0A0A] bg-[#F4F4F0]">
      <div className="grid grid-cols-4 gap-1.5 px-3 py-2 sm:grid-cols-2 sm:gap-2 sm:py-3">
        {VALUE_LANES.map(lane => {
          const isActive = activeLaneId === lane.id;
          return (
            <button
              key={lane.id}
              onClick={() => onLaneSelect(lane)}
              className={`min-h-[42px] px-1 py-1 border-3 border-[#0A0A0A] font-display text-[10px] leading-[1.05] uppercase tracking-wide transition-all sm:min-h-[48px] sm:px-2 sm:py-2 sm:text-sm ${
                isActive
                  ? 'shadow-none translate-x-[2px] translate-y-[2px]'
                  : 'shadow-[3px_3px_0_#0A0A0A] hover:-translate-y-[1px]'
              }`}
              style={{
                background: isActive ? lane.accent : '#FFFFFF',
                color: isActive ? lane.textOnAccent : '#0A0A0A',
                borderWidth: '3px',
              }}
            >
              {lane.title}
            </button>
          );
        })}
      </div>
      {activeLane && activeLane.submodes && (
        <div className="grid grid-cols-4 gap-1 px-3 pb-2 sm:grid-cols-2 sm:gap-1.5">
          {activeLane.submodes.map(s => {
            const isOn = selectedMode === s.id;
            return (
              <button
                key={s.id}
                onClick={() => onSubmodeSelect(s.id)}
                className={`min-h-[28px] px-1 py-1 border-2 border-[#0A0A0A] font-mono text-[8px] leading-[1.05] uppercase tracking-tight transition-colors sm:min-h-[32px] sm:px-2 sm:text-[10px] sm:tracking-wide ${
                  isOn ? 'bg-[#0A0A0A] text-[#FFE000]' : 'bg-white text-[#0A0A0A] hover:bg-[#FFE000]'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      )}
      {visibleExamples.length > 0 && !hasInput && (
        <div className="px-3 pb-2">
          <span className="block font-mono text-[9px] text-[#0A0A0A]/50 uppercase mb-1">Try:</span>
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            {visibleExamples.map((ex, i) => (
              <button
                key={i}
                onClick={() => onExampleTap(ex)}
                className="shrink-0 max-w-[78%] min-h-[32px] px-2 py-1.5 bg-white border border-[#0A0A0A]/40 font-mono text-[9px] sm:text-[10px] text-[#0A0A0A]/80 hover:bg-[#FFE000]/30 hover:border-[#0A0A0A] transition-colors leading-snug text-left"
              >
                "{ex}"
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeSheet({ selectedMode, onSelect, onClose }) {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col justify-end"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Sheet */}
      <motion.div
        className="relative bg-[#F4F4F0] border-t-4 border-[#0A0A0A] max-h-[75vh] flex flex-col"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 220 }}
      >
        {/* Handle */}
        <div className="flex items-center justify-between px-4 py-3 border-b-4 border-[#0A0A0A] shrink-0">
          <span className="font-display text-2xl uppercase tracking-tight">Pick a Vibe</span>
          <button
            data-testid="mode-sheet-close"
            onClick={onClose}
            className="w-8 h-8 border-4 border-[#0A0A0A] bg-[#FF0066] text-white flex items-center justify-center hover:bg-[#0A0A0A] transition-colors"
          >
            <X className="w-4 h-4 stroke-[3]" />
          </button>
        </div>

        {/* Mode list */}
        <div className="overflow-y-auto flex-1 p-4 flex flex-col gap-4">
          {Object.entries(groupedModes).map(([category, modes]) => (
            <div key={category}>
              <p className="font-display text-sm uppercase tracking-widest text-[#0A0A0A]/50 mb-2">{category}</p>
              <div className="flex flex-wrap gap-2">
                {modes.map(mode => (
                  <button
                    key={mode.id}
                    data-testid="mode-selector-item"
                    onClick={() => { onSelect(mode.id); onClose(); }}
                    className={`font-display text-lg uppercase px-4 py-2 border-4 border-[#0A0A0A] transition-all ${
                      selectedMode === mode.id
                        ? 'bg-[#0A0A0A] text-[#FFE000] shadow-none translate-x-[3px] translate-y-[3px]'
                        : 'bg-white shadow-brutal-tiny hover:bg-[#FFE000] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]'
                    }`}
                  >
                    {mode.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

function SignInGate({ onUnlock }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const cleanEmail = normalizeEmail(email);

    if (!isPlausibleEmail(cleanEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    setSending(true);
    setError('');

    try {
      const response = await axios.post(`${API}/submit-email`, { email: cleanEmail });
      if (response.data?.success !== true || response.data?.persisted !== true) {
        throw new Error('Email was not saved');
      }

      try {
        localStorage.setItem(EMAIL_GATE_STORAGE_KEY, '1');
        localStorage.setItem(EMAIL_STORAGE_KEY, cleanEmail);
      } catch {
        // Storage can be unavailable in private browsing. The current session still unlocks.
      }
      onUnlock(cleanEmail);
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save your email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="min-h-[100dvh] bg-[#0A0A0A] text-white flex items-center justify-center px-4 py-8">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm border-4 border-[#FFE000] bg-[#0A0A0A] p-5 sm:p-7 shadow-[8px_8px_0_#FF0066]"
        noValidate
      >
        <h1 className="font-display text-5xl sm:text-6xl text-[#FFE000] uppercase text-center leading-none">
          VIBE<br />TRANSLATOR
        </h1>
        <p className="font-mono text-sm text-[#00FF88] text-center mt-4 mb-6">
          Enter your email to start translating.
        </p>

        <div className="flex flex-col gap-2">
          <label htmlFor="gateway-email" className="font-display text-base uppercase tracking-wider text-white">
            Email address
          </label>
          <input
            id="gateway-email"
            data-testid="email-gate-input"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck="false"
            maxLength={MAX_EMAIL_LENGTH}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (error) setError('');
            }}
            onBlur={() => {
              if (email && !isPlausibleEmail(email)) setError('Enter a valid email address.');
            }}
            aria-invalid={Boolean(error)}
            aria-describedby="gateway-email-help gateway-email-error"
            placeholder="you@example.com"
            autoFocus
            className="w-full border-4 border-[#FFE000] bg-white text-[#0A0A0A] font-mono px-4 py-3 placeholder:text-[#0A0A0A]/50 focus:border-[#00FF88]"
            style={{ fontSize: '16px' }}
          />
          <p id="gateway-email-help" className="font-mono text-[11px] text-white/70">
            Beta access only. No password or account setup.
          </p>
          <p id="gateway-email-error" role="alert" className="min-h-5 font-mono text-sm text-[#FF5C98]">
            {error}
          </p>
        </div>

        <button
          data-testid="email-gate-submit"
          type="submit"
          disabled={sending}
          className="mt-2 w-full border-4 border-[#FFE000] bg-[#FFE000] text-[#0A0A0A] font-display text-xl uppercase py-3 shadow-[4px_4px_0_#FF0066] hover:bg-[#00FF88] hover:border-[#00FF88] active:translate-x-1 active:translate-y-1 active:shadow-none disabled:cursor-wait disabled:opacity-60"
        >
          {sending ? 'SAVING...' : 'LET ME IN'}
        </button>
        <p className="font-mono text-[10px] text-white/60 text-center mt-4">
          We store your email for beta access. No spam.
        </p>
      </form>
    </main>
  );
}

// --- Main App ---
function VibeApp({ email }) {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  // selectedMode default matches the first Decode lane's primary mode so the
  // landing experience reads as a real translator, not a meme picker.
  const [selectedMode, setSelectedMode] = useState('female_to_male');
  const [isTranslating, setIsTranslating] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState('');
  const [showModeSheet, setShowModeSheet] = useState(false);
  const [outputKey, setOutputKey] = useState(0);
  const [showStoryTour, setShowStoryTour] = useState(() => {
    try { return sessionStorage.getItem('vibeStoryTourSeenV1') !== '1'; }
    catch { return true; }
  });
  // Surface state — 'decode' is the value-focused web experiment, 'play' keeps the
  // viral meme experience intact for sharing/attention.
  const [activeSurface, setActiveSurface] = useState('decode');
  const [activeLaneId, setActiveLaneId] = useState('decode_them');

  const startTour = () => setShowStoryTour(true);
  const closeTour = () => {
    try { sessionStorage.setItem('vibeStoryTourSeenV1', '1'); } catch { /* Session storage can be unavailable. */ }
    setShowStoryTour(false);
  };

  const MAX_LENGTH = 200;
  const charCount = inputText.length;
  const isNearLimit = charCount > 160;
  const selectedModeName = TRANSLATION_MODES.find(m => m.id === selectedMode)?.name || 'Select Mode';
  const selectedModeKind = TRANSLATION_MODES.find(m => m.id === selectedMode)?.kind || 'translate';
  const goActionLabel = 'GO';
  const goLoadingLabel = 'GOING';

  useEffect(() => {
    if (!inputText.trim()) { setOutputText(''); setError(''); }
  }, [inputText]);

  const handleSuggestionSelect = (s) => {
    setInputText(s.text);
    setSelectedMode(s.mode);
    setOutputText('');
    setError('');
  };

  const handleFeelingLucky = () => {
    const r = SUGGESTIONS[Math.floor(Math.random() * SUGGESTIONS.length)];
    setInputText(r.text);
    setSelectedMode(r.mode);
    setOutputText('');
    setError('');
  };

  const handleSurfaceChange = (surface) => {
    setActiveSurface(surface);
    if (surface === 'play') {
      // Drop lane context but preserve any text the user has already typed.
      setActiveLaneId(null);
    }
  };

  const handleLaneSelect = (lane) => {
    if (lane.routeTo === 'play') {
      handleSurfaceChange('play');
      return;
    }
    setActiveLaneId(lane.id);
    setSelectedMode(lane.primaryMode);
    setOutputText('');
    setError('');
    const examples = MODE_EXAMPLES[lane.primaryMode] || [];
    if ((!inputText.trim() || Object.values(MODE_EXAMPLES).flat().includes(inputText)) && examples.length) {
      setInputText(examples[0]);
    }
  };

  const handleSubmodeSelect = (modeId) => {
    setSelectedMode(modeId);
    setOutputText('');
    setError('');
    const examples = MODE_EXAMPLES[modeId] || [];
    if ((!inputText.trim() || Object.values(MODE_EXAMPLES).flat().includes(inputText)) && examples.length) {
      setInputText(examples[0]);
    }
  };

  const activeLane = VALUE_LANES.find(l => l.id === activeLaneId) || null;
  const placeholder = activeSurface === 'decode' && activeLane?.placeholder
    ? activeLane.placeholder
    : (MODE_HINTS[selectedMode] || "Type anything — we'll translate it...");

  const handleTranslate = async (overrideText, overrideMode) => {
    const text = overrideText || inputText;
    const mode = overrideMode || selectedMode;
    if (!text.trim() || text.length > MAX_LENGTH || isTranslating) return;
    setIsTranslating(true);
    setError('');
    setOutputText('');
    try {
      const res = await axios.post(`${API}/translate`, { text, mode, email });
      setOutputText(res.data.translated);
      setOutputKey(k => k + 1);
    } catch (err) {
      setError(err.response?.status === 429 ? 'Slow down! Too many requests.' : 'Something broke. Try again.');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleCopy = async () => {
    if (!outputText) return;
    try { await navigator.clipboard.writeText(outputText); setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); } catch {}
  };

  const handleShare = async () => {
    if (!outputText) return;
    if (navigator.share) { try { await navigator.share({ text: outputText, title: 'Vibe Translator' }); } catch {} }
    else handleCopy();
  };

  return (
    <>
      {/* Onboarding Tour — only meaningful on the Play surface (it points at Play UI). */}
      <AnimatePresence>
        {showStoryTour && <VibeStoryTour onClose={closeTour} />}
      </AnimatePresence>

      {/* App shell — phone-card layout */}
      <div className="app-shell v1-app-shell w-full max-w-lg mx-auto flex flex-col bg-[#F4F4F0] text-[#0A0A0A] overflow-x-hidden sm:overflow-hidden sm:border-x-4 sm:border-[#0A0A0A] relative">

        {/* HEADER */}
        <header className="shrink-0 bg-[#FFE000] border-b-4 border-[#0A0A0A] shadow-[0_4px_0_#0A0A0A] px-3 py-2 sm:py-1.5 flex items-center justify-between relative z-10">
          <h1 className="font-display text-[1.75rem] sm:text-6xl uppercase leading-none -rotate-1 sm:-rotate-2 whitespace-nowrap">
            <span>VIBE</span><br className="hidden sm:block" /><span className="ml-2 sm:ml-0">TRANSLATOR</span>
          </h1>
          <div className="flex flex-col items-end gap-0.5 sm:gap-1.5">
            <span className="font-display text-[9px] sm:text-sm bg-[#FF0066] text-white px-1.5 py-0.5 sm:px-3 sm:py-1 border-2 sm:border-4 border-[#0A0A0A] rotate-6 sm:rotate-12 shadow-[2px_2px_0_#0A0A0A] sm:shadow-brutal-medium">
              💀 FR FR 💀
            </span>
            <button
              data-testid="start-tour-btn"
              onClick={startTour}
              className="font-display text-[7px] sm:text-[10px] uppercase tracking-wider sm:tracking-widest bg-[#0A0A0A] text-[#FFE000] px-1.5 py-0.5 sm:px-2 sm:py-1 border-2 border-[#0A0A0A] hover:bg-[#FFE000] hover:text-[#0A0A0A] transition-colors"
            >
              ? HOW IT WORKS
            </button>
          </div>
        </header>

        {/* SURFACE TABS — Decode (real talk) vs Play (meme mode) */}
        <SurfaceTabs active={activeSurface} onChange={handleSurfaceChange} />

        {/* SURFACE-SPECIFIC HEADER ROW */}
        {activeSurface === 'play' ? (
          <Carousel onSelect={handleSuggestionSelect} />
        ) : (
          <DecodeLanes
            activeLaneId={activeLaneId}
            onLaneSelect={handleLaneSelect}
            onSubmodeSelect={handleSubmodeSelect}
            selectedMode={selectedMode}
            onExampleTap={(text) => setInputText(text)}
            hasInput={Boolean(inputText.trim())}
          />
        )}

        {/* MIDDLE SECTION — fixed input + GO + flex output (shared across surfaces) */}
        <div className="flex-1 min-h-0 flex flex-col px-3 pt-2 pb-3 gap-1.5 sm:pt-3 sm:gap-2">

          {/* Mode selector — only on Play. Decode uses the lane chips above. */}
          {activeSurface === 'play' && (
            <button
              data-testid="mode-selector-trigger"
              onClick={() => setShowModeSheet(true)}
              className="self-start bg-[#FFE000]/10 border-4 border-[#0A0A0A] shadow-[4px_4px_0_#0A0A0A] px-3 py-2 font-display text-base uppercase inline-flex items-center gap-2 hover:bg-[#FFE000] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all"
            >
              <span className="font-mono text-[10px] text-[#0A0A0A]/50 font-normal tracking-widest">MODE</span>
              <span>{selectedModeName}</span>
              <ChevronDown className="w-4 h-4 stroke-[3]" />
            </button>
          )}

          {/* Input textarea — fixed compact height, char counter inside */}
          <div className="shrink-0 border-4 border-[#0A0A0A] bg-white shadow-[10px_10px_0_#0A0A0A] relative">
            <div className="absolute top-2 right-2 z-10">
              <span className={`font-mono text-[10px] px-2 py-0.5 border-2 border-[#0A0A0A] ${isNearLimit ? 'bg-[#FF0066] text-white' : 'bg-[#F4F4F0]'}`}>
                {charCount}/{MAX_LENGTH}
              </span>
            </div>
            <textarea
              data-testid="input-textarea"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              placeholder={placeholder}
              maxLength={MAX_LENGTH}
              className="w-full h-[92px] sm:h-[104px] p-3 pr-20 resize-none outline-none font-mono text-lg sm:text-xl leading-relaxed bg-transparent text-[#0A0A0A] placeholder-[#0A0A0A]/40 block"
            />
          </div>

          {/* GO + Lucky */}
          <div className="shrink-0 flex gap-2">
            <motion.button
              data-testid="translate-go-button"
              onClick={() => handleTranslate()}
              disabled={!inputText.trim() || isTranslating}
              animate={isTranslating ? { opacity: [1, 0.75, 1], transition: { duration: 1.8, repeat: Infinity, ease: 'easeInOut' } } : { opacity: 1 }}
              whileHover={{ rotate: -1, scale: 1.01 }}
              whileTap={{ scale: 0.97 }}
              className="flex-1 border-4 border-[#0A0A0A] bg-[#FF0066] shadow-[6px_6px_0_#0A0A0A] font-display text-2xl text-white uppercase flex items-center justify-center gap-2 py-1.5 disabled:opacity-40 transition-shadow"
            >
              {isTranslating ? (
                <span className="flex gap-2 items-center">
                  {goLoadingLabel}
                  <span className="flex gap-1">
                    <span className="animate-bounce" style={{ animationDelay: '0ms' }}>·</span>
                    <span className="animate-bounce" style={{ animationDelay: '150ms' }}>·</span>
                    <span className="animate-bounce" style={{ animationDelay: '300ms' }}>·</span>
                  </span>
                </span>
              ) : (
                <>
                  <Sparkles className="w-6 h-6" />
                  {goActionLabel}
                </>
              )}
            </motion.button>
            <button
              data-testid="random-lucky-button"
              onClick={handleFeelingLucky}
              className="w-14 bg-[#00FF88] border-4 border-[#0A0A0A] shadow-[6px_6px_0_#0A0A0A] flex items-center justify-center hover:shadow-[3px_3px_0_#0A0A0A] hover:translate-x-[3px] hover:translate-y-[3px] transition-all"
            >
              <Shuffle className="w-5 h-5 stroke-[2.5]" />
            </button>
          </div>

          {/* Output box — fills all remaining space */}
          <div className="flex-1 border-4 border-[#0A0A0A] bg-[#FFE000] shadow-[10px_10px_0_#0A0A0A] flex flex-col relative overflow-hidden min-h-[180px] sm:min-h-[140px]">
            <AnimatePresence mode="wait">
              {error ? (
                <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1 flex items-center justify-center p-4">
                  <p data-testid="error-msg" className="font-mono text-sm text-[#FF0066] text-center font-bold">{error}</p>
                </motion.div>
              ) : outputText ? (
                <motion.div
                  key={outputKey}
                  initial={{ scale: 0.92, y: -16, opacity: 0 }}
                  animate={{ scale: 1, y: 0, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 18 }}
                  className="flex-1 overflow-y-auto p-3 pb-12"
                >
                  {/* Keep generated copy comfortably readable at every length. */}

                  <p data-testid="output-translation-text" className="font-mono font-bold text-[#0A0A0A] text-xl sm:text-2xl leading-relaxed whitespace-pre-wrap">
                    {outputText}
                  </p>
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1 flex items-center justify-center p-4">
                  <p className="font-mono text-sm text-[#0A0A0A]/40 text-center">
                    {isTranslating ? 'translating your vibe...' : '↑ hit GO and watch the magic'}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Copy / Share */}
            <AnimatePresence>
              {outputText && !error && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  className="absolute bottom-2 right-2 flex gap-1.5"
                >
                  <button
                    data-testid="copy-result-button"
                    onClick={handleCopy}
                    className="bg-white text-[#0A0A0A] border-4 border-[#0A0A0A] px-3 py-1.5 font-display text-sm shadow-brutal-tiny hover:bg-[#0A0A0A] hover:text-white hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex items-center gap-1"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {copySuccess ? 'COPIED!' : 'COPY'}
                  </button>
                  <button
                    data-testid="share-result-button"
                    onClick={handleShare}
                    className="bg-[#FF0066] text-white border-4 border-[#0A0A0A] px-3 py-1.5 font-display text-sm shadow-brutal-tiny hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex items-center gap-1"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    SHARE
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>

        {/* FOOTER */}
        <footer className="shrink-0 border-t-4 border-[#0A0A0A] bg-[#0A0A0A] py-1.5 px-4 flex items-center justify-between">
          <p className="font-display text-[10px] text-[#00FF88] uppercase tracking-[0.25em]">
            No cap, fr fr — built for the memes
          </p>
          <div className="flex items-center gap-3">
            <button onClick={startTour} data-testid="restart-tour-btn" className="text-[#00FF88]/40 hover:text-[#00FF88] transition-colors" title="How to use">
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
            <p className="font-mono text-[10px] text-[#00FF88]">
              <a href="https://www.linkedin.com/in/terencela" target="_blank" rel="noopener noreferrer" className="hover:underline">© TERENCE LA</a>
            </p>
          </div>
        </footer>
      </div>

      {/* MODE SHEET OVERLAY */}
      <AnimatePresence>
        {showModeSheet && (
          <ModeSheet
            selectedMode={selectedMode}
            onSelect={id => { setSelectedMode(id); setOutputText(''); setError(''); }}
            onClose={() => setShowModeSheet(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default function App() {
  const [savedEmail, setSavedEmail] = useState(() => {
    try {
      const email = localStorage.getItem(EMAIL_STORAGE_KEY) || '';
      return isPlausibleEmail(email) ? normalizeEmail(email) : '';
    } catch {
      return '';
    }
  });

  if (!savedEmail) {
    return <SignInGate onUnlock={setSavedEmail} />;
  }

  return <VibeApp email={savedEmail} />;
}
